import { AccessControl, NativeBiometric } from '@capgo/capacitor-native-biometric';
import { platform } from './platform';
import { secureStorage } from './secureStorage';

/**
 * A remembered session bound to the device biometric. With biometric unlock on (an explicit
 * opt-in in Settings) the session's refresh and SSO session tokens live only in one Keychain /
 * Keystore item created with `biometryCurrentSet` access control: the OS hands it back after a
 * Face ID / fingerprint match on this device, and destroys it when the enrolled biometrics change.
 * No password is ever stored.
 *
 * Writing needs no prompt (iOS re-adds the item; Android encrypts with the public half of a
 * biometric-bound key pair), so every token rotation rewrites the item silently. Only reading it
 * back prompts.
 */

const KEY = 'member_session';

/** What builds before the biometric session stored: the member's email and password. */
const STORED_LOGIN_SERVER = 'ip4x-resident-login';
const STORED_LOGIN_HINT_KEY = 'biometric_login_username';

export interface BiometricSession {
  refreshToken: string;
  ssoSessionToken: string | null;
}

/** The plugin's rejection code when the item is gone (deleted, or invalidated by a biometric change). */
export const BIOMETRIC_SESSION_GONE = '21';

export async function saveBiometricSession(session: BiometricSession): Promise<void> {
  await NativeBiometric.setData({
    key: KEY,
    value: JSON.stringify(session),
    accessControl: AccessControl.BIOMETRY_CURRENT_SET,
  });
}

/** Whether a bound session is stored. Never prompts. */
export async function hasBiometricSession(): Promise<boolean> {
  if (!platform.isNative()) return false;

  const { isSaved } = await NativeBiometric.isDataSaved({ key: KEY });
  return isSaved;
}

/**
 * Prompt the biometric and hand back the bound session. Rejects with the plugin's error code when
 * the member cancels, the match fails, or the item is gone (`BIOMETRIC_SESSION_GONE`).
 */
export async function readBiometricSession(reason: string): Promise<BiometricSession> {
  const { value } = await NativeBiometric.getSecureData({
    key: KEY,
    reason,
    title: 'Unlock',
    description: reason,
  });
  return JSON.parse(value) as BiometricSession;
}

export async function clearBiometricSession(): Promise<void> {
  if (platform.isNative()) await NativeBiometric.deleteData({ key: KEY });
}

/**
 * Delete the email and password builds before the biometric session kept behind the biometric.
 * The Keychain outlives app updates, so an upgraded device still holds them until this runs.
 */
export async function deleteStoredPassword(): Promise<void> {
  if (!platform.isNative()) return;

  await NativeBiometric.deleteCredentials({ server: STORED_LOGIN_SERVER });
  await secureStorage.remove(STORED_LOGIN_HINT_KEY);
}
